import { useCallback, useState } from "react";

function useAttachmentUpload({ cloudinaryUploadUrl, cloudinaryPreset, maxFiles = 5 }) {
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const fileToBase64 = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.onloadend = () => resolve(fileReader.result);
        fileReader.onerror = reject;
        fileReader.readAsDataURL(file);
      }),
    [],
  );

  const uploadAttachmentToCloudinary = useCallback(
    async (file) => {
      if (!cloudinaryUploadUrl || !cloudinaryPreset) {
        throw new Error("Cloudinary configuration is missing");
      }
      const fileBase64 = await fileToBase64(file);
      const mediaType = file.type?.startsWith("video/") ? "video" : "image";
      const apiUrl = `${cloudinaryUploadUrl.replace(/\/$/, "")}/${mediaType}/upload`;

      const response = await fetch(apiUrl, {
        body: JSON.stringify({
          file: fileBase64,
          upload_preset: cloudinaryPreset,
        }),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      });
      if (!response.ok) {
        throw new Error("Attachment upload failed");
      }
      const uploaded = await response.json();
      if (!uploaded?.secure_url) {
        throw new Error("No attachment URL returned");
      }
      return {
        name: file.name,
        url: uploaded.secure_url,
      };
    },
    [cloudinaryUploadUrl, cloudinaryPreset, fileToBase64],
  );

  const handleFileSelection = useCallback(
    async (event) => {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      setUploadError("");
      const imageOnlyFiles = files.filter((file) =>
        (file.type || "").startsWith("image/"),
      );
      if (imageOnlyFiles.length !== files.length) {
        setUploadError("Only image files are allowed.");
      }
      if (!imageOnlyFiles.length) {
        event.target.value = "";
        return;
      }
      setUploadingAttachments(true);
      try {
        const allowedFiles = imageOnlyFiles.slice(0, maxFiles);
        const uploadedFiles = await Promise.all(
          allowedFiles.map((file) => uploadAttachmentToCloudinary(file)),
        );
        setSelectedAttachments((prev) => [...prev, ...uploadedFiles].slice(0, maxFiles));
      } catch (error) {
        setUploadError(error.message || "Failed to upload attachments. Please try again.");
      } finally {
        setUploadingAttachments(false);
        event.target.value = "";
      }
    },
    [maxFiles, uploadAttachmentToCloudinary],
  );

  const removeAttachment = useCallback((index) => {
    setSelectedAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearAttachments = useCallback(() => {
    setSelectedAttachments([]);
    setUploadError("");
  }, []);

  return {
    selectedAttachments,
    uploadingAttachments,
    uploadError,
    setUploadError,
    handleFileSelection,
    removeAttachment,
    clearAttachments,
  };
}

export default useAttachmentUpload;
